import { useSyncExternalStore } from "react";

/**
 * 動きを減らす設定。端末の設定（prefers-reduced-motion）に加えて、画面の中でも「減らす」を選べる。
 * 端末で減らしているときは、画面の中で増やすことはできない。
 * 結果は <html data-motion="reduce|full"> に出し、CSS（src/styles/motion.css）と MotionRoot が見る。
 * 選んだ値は、この端末のブラウザーにだけ覚える（保存できなくても動く）。
 */

export type MotionChoice = "system" | "reduce";

const KEY = "music-community:motion";
const listeners = new Set<() => void>();
let choice: MotionChoice = readChoice();
const media = typeof window !== "undefined" && window.matchMedia ? window.matchMedia("(prefers-reduced-motion: reduce)") : null;

function readChoice(): MotionChoice {
  try {
    return localStorage.getItem(KEY) === "reduce" ? "reduce" : "system";
  } catch {
    return "system";
  }
}

export function systemReduces(): boolean {
  return !!media?.matches;
}

export function isReduced(): boolean {
  return choice === "reduce" || systemReduces();
}

function apply() {
  if (typeof document === "undefined") return;
  document.documentElement.dataset.motion = isReduced() ? "reduce" : "full";
  listeners.forEach((fn) => fn());
}

media?.addEventListener?.("change", apply);
apply();

export function getMotionChoice(): MotionChoice {
  return choice;
}

export function setMotionChoice(next: MotionChoice) {
  choice = next;
  try {
    if (next === "reduce") localStorage.setItem(KEY, "reduce");
    else localStorage.removeItem(KEY);
  } catch {
    // 覚えられなくても、このページのあいだは効く
  }
  apply();
}

function subscribe(fn: () => void) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function useReducedMotion(): boolean {
  return useSyncExternalStore(subscribe, isReduced, () => false);
}

export function useMotionChoice(): MotionChoice {
  return useSyncExternalStore(subscribe, getMotionChoice, () => "system" as MotionChoice);
}
